import { useState } from 'react';
import type { CityDto } from '@waylo/shared';
import { useCityResults } from '../lib/useCityResults';
import { COUNTRY_CHIPS } from '../lib/countries';

interface Props {
  /** Selected city id, or '' when none. */
  value: string;
  /** The selected city object (for display when collapsed). */
  selectedCity?: CityDto | null;
  onChange: (id: string, city: CityDto) => void;
  /** Language-gated scope: 'RS' | 'HR' | 'BA' | 'all'. */
  country: string;
  nameKey: 'nameEn' | 'nameSr';
  label: string;
  placeholder?: string;
  /** A city id to hide from suggestions (e.g. the other endpoint). */
  excludeId?: string;
  emptyText?: string;
}

/**
 * Single-select city picker. Tapping opens a full-screen search (input pinned
 * at the top, scrollable results) backed by the server search endpoint. In
 * English mode (country='all') a country chip row lets the user narrow scope.
 */
export function CityPicker({
  value,
  selectedCity,
  onChange,
  country,
  nameKey,
  label,
  placeholder = 'Type a city name…',
  excludeId,
  emptyText = 'No matches.',
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [chip, setChip] = useState<string>('all'); // only used when country==='all'

  const scope = country === 'all' ? chip : country;
  const { results, loading } = useCityResults(q, scope, open);
  const matches = results.filter((c) => c.id !== excludeId);

  const openSearch = (): void => {
    setQ('');
    setOpen(true);
  };
  const pick = (c: CityDto): void => {
    onChange(c.id, c);
    setOpen(false);
  };

  return (
    <div className="block w-full">
      <span className="mb-1.5 block text-sm font-medium text-tg-text">{label}</span>
      <button
        type="button"
        onClick={openSearch}
        className="w-full rounded-input border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-3 text-left text-base outline-none"
      >
        <span className={selectedCity ? 'text-tg-text' : 'text-tg-hint'}>
          {selectedCity?.[nameKey] ?? placeholder}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[1100] flex flex-col bg-tg-bg">
          <div
            className="flex items-center gap-2 border-b border-[var(--border-light)] p-3"
            style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0))' }}
          >
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              className="min-w-0 flex-1 rounded-input border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2.5 text-base text-tg-text outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="flex-none px-2 py-2 text-base font-medium text-tg-link"
            >
              ✕
            </button>
          </div>

          {country === 'all' && (
            <div className="flex gap-1.5 overflow-x-auto border-b border-[var(--border-light)] px-3 py-2">
              {COUNTRY_CHIPS.map((cc) => (
                <button
                  key={cc.code}
                  type="button"
                  onClick={() => setChip(cc.code)}
                  className={
                    'flex-none rounded-pill px-3 py-1 text-xs font-medium transition ' +
                    (chip === cc.code
                      ? 'bg-[var(--border-medium)] font-semibold text-tg-text'
                      : 'bg-tg-bg-secondary text-tg-hint')
                  }
                >
                  {cc.label}
                </button>
              ))}
            </div>
          )}

          <ul className="flex-1 overflow-auto">
            {matches.length === 0 ? (
              <li className="p-4 text-center text-sm text-tg-hint">{loading ? '…' : emptyText}</li>
            ) : (
              matches.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    className={
                      'block w-full border-b border-[var(--border-light)] px-4 py-3 text-left text-base ' +
                      (c.id === value ? 'font-semibold text-tg-text' : 'text-tg-text')
                    }
                  >
                    {c[nameKey]}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
